import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, FileText, ScrollText, Gavel, Scale, FileSignature, Clock, AlertTriangle, Mail, Phone, ChevronRight } from 'lucide-react';

const TermsOfService = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero section */}
      <div className="relative bg-gradient-to-b from-indigo-900 to-purple-900 py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]"></div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Terms of Service</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The agreement governing your use of the LoadLink platform and services
            </p>
            <div className="flex justify-center mt-6">
              <div className="inline-flex items-center gap-2 text-sm text-indigo-300 bg-indigo-500/10 rounded-full px-4 py-1 border border-indigo-500/20">
                <Clock className="w-4 h-4" />
                Last Updated: {new Date().toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'})}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md">
          <div className="p-6 md:p-8 lg:p-10">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="prose prose-lg max-w-none text-gray-700"
            >
              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mb-4">
                  <ScrollText className="w-6 h-6 text-indigo-600" />
                  1. Agreement to Terms
                </h2>
                <p>
                  By accessing or using the LoadLink platform ("Platform"), which includes our website, mobile applications, and all related services, you agree to be bound by these Terms of Service ("Terms"). These Terms constitute a legally binding agreement between you and LoadLink Company ("LoadLink," "we," "us," or "our").
                </p>
                <p>
                  If you are using the Platform on behalf of a company or other legal entity, you represent that you have the authority to bind such entity to these Terms. If you do not have such authority or do not agree with these Terms, you must not accept these Terms or access or use the Platform.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <FileSignature className="w-6 h-6 text-indigo-600" />
                  2. Platform Access and Account Registration
                </h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Registration Requirements</h3>
                <p>To use our Platform, you must:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Complete the registration process</li>
                  <li>Provide accurate, current, and complete information</li>
                  <li>Be at least 18 years old</li>
                  <li>Have the legal capacity to enter into binding contracts</li>
                  <li>Maintain and promptly update your information</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Account Security</h3>
                <p>You are responsible for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Immediately notifying us of any unauthorized access</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.3 Account Types</h3>
                <p>We offer different account types based on your role in the logistics process:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Customer:</strong> For shippers and freight brokers</li>
                  <li><strong>Carrier:</strong> For transportation companies and fleet operators</li>
                  <li><strong>Driver:</strong> For individual drivers associated with carriers</li>
                </ul>
                <p>Each account type has specific features, permissions, and requirements.</p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Gavel className="w-6 h-6 text-indigo-600" />
                  3. Platform Rules and Prohibited Activities
                </h2>
                <p>You agree not to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe on the rights of others</li>
                  <li>Post false, inaccurate, misleading, or defamatory content</li>
                  <li>Use the Platform for any illegal or unauthorized purpose</li>
                  <li>Interfere with or disrupt the Platform or servers</li>
                  <li>Attempt to gain unauthorized access to any part of the Platform</li>
                  <li>Engage in fraudulent activities or misrepresent your identity</li>
                  <li>Use the Platform to transmit malware or other harmful code</li>
                  <li>Scrape, data mine, or extract data from the Platform without permission</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Scale className="w-6 h-6 text-indigo-600" />
                  4. Transaction Terms
                </h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.1 Load Posting and Bidding</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Customers may post load details for carriers to bid on</li>
                  <li>Carriers may submit bids on available loads</li>
                  <li>All information provided must be accurate and complete</li>
                  <li>We do not guarantee that loads will be accepted or completed</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.2 Contracts and Agreements</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>When a bid is accepted, a binding agreement is formed between customer and carrier</li>
                  <li>We facilitate but are not a party to these agreements</li>
                  <li>All parties are responsible for fulfilling their obligations</li>
                  <li>Agreements are subject to the terms specified in the load details and accepted bid</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">4.3 Payments and Fees</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Payments for services are processed through our Platform</li>
                  <li>We charge service fees as detailed in our pricing documentation</li>
                  <li>All fees are non-refundable unless otherwise specified</li>
                  <li>You are responsible for taxes associated with your use of the Platform</li>
                </ul>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Shield className="w-6 h-6 text-indigo-600" />
                  5. Liability and Insurance
                </h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.1 Carrier Insurance Requirements</h3>
                <p>Carriers must maintain valid insurance coverage including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Commercial Auto Liability</li>
                  <li>Cargo Insurance</li>
                  <li>General Liability Insurance</li>
                  <li>Workers' Compensation (where required by law)</li>
                </ul>
                <p>Insurance documentation must be uploaded and kept current on the Platform.</p>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">5.2 Liability for Services</h3>
                <p>
                  Carriers are solely responsible for the transportation services provided, including any loss or damage to cargo, delays, or other service failures. LoadLink does not assume liability for the acts or omissions of carriers, drivers, or customers using our Platform.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Lock className="w-6 h-6 text-indigo-600" />
                  6. Intellectual Property
                </h2>
                <p>
                  The Platform, including all content, features, and functionality, is owned by LoadLink and is protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p>
                  You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any materials from our Platform without our prior written consent.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <AlertTriangle className="w-6 h-6 text-indigo-600" />
                  7. Disclaimers and Limitations of Liability
                </h2>
                
                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.1 Platform Provided "As Is"</h3>
                <p>
                  The Platform is provided on an "as is" and "as available" basis, without warranties of any kind, either express or implied. We do not guarantee that the Platform will be uninterrupted, error-free, or secure.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.2 Limitation of Liability</h3>
                <p>
                  To the maximum extent permitted by law, LoadLink shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Platform.
                </p>
                <p>
                  Our total liability for all claims related to these Terms shall not exceed the amounts paid by you to LoadLink during the three (3) months preceding the event giving rise to the liability.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Gavel className="w-6 h-6 text-indigo-600" />
                  8. Dispute Resolution
                </h2>
                <p>
                  Any dispute arising out of or relating to these Terms or the Platform shall be resolved through binding arbitration in accordance with the Commercial Arbitration Rules of the American Arbitration Association.
                </p>
                <p>
                  The arbitration shall be conducted in Warren, Michigan, and the judgment on the award rendered by the arbitrator(s) may be entered in any court having jurisdiction thereof.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Clock className="w-6 h-6 text-indigo-600" />
                  9. Term and Termination
                </h2>
                <p>
                  These Terms remain in full force and effect while you use the Platform. We may terminate or suspend your account and access to the Platform immediately, without prior notice, for any reason, including if you breach these Terms.
                </p>
                <p>
                  Upon termination, your right to use the Platform will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <ScrollText className="w-6 h-6 text-indigo-600" />
                  10. Changes to Terms
                </h2>
                <p>
                  We may modify these Terms at any time by posting the revised Terms on the Platform. Your continued use of the Platform after any such changes constitutes your acceptance of the new Terms.
                </p>
                <p>
                  We will provide notification of material changes to these Terms through the Platform or via email. It is your responsibility to review these Terms periodically.
                </p>
              </motion.div>

              <motion.div variants={itemVariants}>
                <h2 className="text-2xl font-bold text-indigo-900 flex items-center gap-2 mt-8 mb-4">
                  <Mail className="w-6 h-6 text-indigo-600" />
                  11. Contact Information
                </h2>
                <p>If you have questions or concerns about these Terms, please contact us at:</p>
                <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p><strong>Email:</strong> <a href="mailto:joe@loadlinkco.com" className="text-indigo-600 hover:text-indigo-800">joe@loadlinkco.com</a></p>
                  <p className="mt-2"><strong>Address:</strong><br />
                    LoadLink Company<br />
                    13950 Frazho Rd<br />
                    Warren, MI 48089
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Related links section */}
        <div className="max-w-4xl mx-auto mt-10">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Related Documents</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link 
              to="/privacy-policy"
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all"
            >
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-indigo-600 mr-3" />
                <span>Privacy Policy</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
            <Link 
              to="/security"
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all"
            >
              <div className="flex items-center">
                <Shield className="w-5 h-5 text-indigo-600 mr-3" />
                <span>Security Policy</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Link>
          </div>
        </div>

        {/* Contact section */}
        <div className="max-w-4xl mx-auto mt-10 bg-indigo-50 rounded-xl p-6 border border-indigo-100">
          <div className="flex flex-col sm:flex-row items-center">
            <div className="mb-6 sm:mb-0 sm:mr-6">
              <div className="bg-indigo-100 p-4 rounded-full">
                <Phone className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Have questions about our terms?</h3>
              <p className="text-gray-600 mb-4">Our legal team is ready to assist with any questions about our terms of service.</p>
              <a 
                href="mailto:legal@loadlink.com" 
                className="inline-flex items-center bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Contact Legal Team
                <ChevronRight className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;